import {
  acquireConnection,
  releaseConnection,
} from "./HubConnectionManager";
import { shouldBridge, registerBridge } from "src/realtime/legacyBridge";

/**
 * Registra un conjunto de listeners en la conexión compartida del hub.
 *
 * Antes este archivo construía una `HubConnection` nueva por cada invocación,
 * resultando en 2-3 websockets abiertos al mismo hub y listeners acumulados por
 * falta de cleanup. Ahora delega al `HubConnectionManager` singleton:
 *
 *   - Una conexión por URL, compartida entre callers (refcounted).
 *   - Los listeners se registran con .on() y se desregistran con .off() en el
 *     cleanup — SOLO los listeners de este caller, la conexión sigue viva para
 *     otros callers.
 *   - Cuando ningún caller queda, la conexión se cierra.
 *   - Backoff exponencial en los reintentos (antes era fijo a 5s).
 */
export default function CreateConnection(
  url,
  functions,
  dispatch,
  connectionCallback,
  nameState,
) {
  // Tiempo real V2: los hubs migrados no abren socket; sus listeners reciben
  // los eventos traducidos desde la conexión única `/hub/tpv` (mismo nombre y
  // argumentos). Con el kill-switch (`tmo:realtimeV2=0`) se abre el hub legacy.
  if (shouldBridge(url)) {
    const cleanup = registerBridge(url, functions);
    dispatch(connectionCallback({ name: nameState, hub: null, connectedHub: true }));
    return cleanup;
  }

  const entry = acquireConnection(url);
  const { connection, startPromise } = entry;

  // Registrar SOLO los listeners de este caller. Otros callers pueden tener
  // sus propios listeners para otros eventos (o el mismo) en la misma conexión.
  functions.forEach((f) => {
    connection.on(f.id, f.operator);
  });

  // Al completar el start (o si ya estaba conectado), notificar a Redux.
  // Si start falló, signalr reintentará en segundo plano con el backoff.
  startPromise
    .then(() => {
      dispatch(
        connectionCallback({
          name: nameState,
          hub: connection,
          connectedHub: connection.state === "Connected",
        }),
      );
    })
    .catch(() => {
      // Ya loggeado en el manager.
    });

  return () => {
    try {
      functions.forEach((f) => {
        connection.off(f.id, f.operator);
      });
    } catch (err) {
      console.warn(`Error al desregistrar listeners de ${url}:`, err);
    }

    releaseConnection(url);
  };
}
